import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { Loader2, AlertTriangle } from "lucide-react";

export default function STLViewer({ file, height = 420 }) {
  const mountRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || !file) return;

    let renderer, controls, frameId, geometry;
    const width = mount.clientWidth;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf1f5f9);

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 5000);

    renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    mount.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xffffff, 0x444444, 1.1));
    const dir = new THREE.DirectionalLight(0xffffff, 1.2);
    dir.position.set(1, 1, 1);
    scene.add(dir);
    const dir2 = new THREE.DirectionalLight(0xffffff, 0.6);
    dir2.position.set(-1, -0.5, -1);
    scene.add(dir2);

    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loader = new STLLoader();
        geometry = loader.parse(e.target.result);
        geometry.computeVertexNormals();
        geometry.center();

        const material = new THREE.MeshStandardMaterial({ color: 0xcbd5e1, metalness: 0.05, roughness: 0.75 });
        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        geometry.computeBoundingSphere();
        const radius = geometry.boundingSphere.radius || 50;
        camera.position.set(0, 0, radius * 3);
        controls.target.set(0, 0, 0);
        controls.update();

        setLoading(false);
        const animate = () => {
          frameId = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();
      } catch (err) {
        setError("Impossibile leggere il file STL.");
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setError("Errore durante la lettura del file.");
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);

    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      camera.aspect = w / height;
      camera.updateProjectionMatrix();
      renderer.setSize(w, height);
    };
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (frameId) cancelAnimationFrame(frameId);
      if (controls) controls.dispose();
      if (geometry) geometry.dispose();
      if (renderer) {
        renderer.dispose();
        if (renderer.domElement && mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
      }
    };
  }, [file, height]);

  return (
    <div className="relative w-full rounded-lg overflow-hidden border border-slate-200" style={{ height }} data-testid="stl-viewer">
      <div ref={mountRef} className="w-full h-full" />
      {loading && !error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
          <span className="text-sm text-slate-500">Caricamento modello 3D...</span>
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-100 gap-2 px-4 text-center">
          <AlertTriangle className="h-6 w-6 text-amber-500" />
          <span className="text-sm text-slate-600">{error}</span>
        </div>
      )}
      {!loading && !error && (
        <span className="absolute bottom-2 right-3 text-xs text-slate-400 select-none">Trascina per ruotare · scroll per zoom</span>
      )}
    </div>
  );
}
